import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "tgi-cookie-consent";

const CATEGORIES = [
  {
    key: "necessary",
    name: "Strictly necessary",
    always: true,
    desc: "Required for the site to function. Includes authentication and session cookies used to keep you signed in and secure your account. These cannot be turned off.",
  },
  {
    key: "functional",
    name: "Functional",
    desc: "Remembers your interface preferences (e.g. your cookie choices themselves). Stored in your browser only.",
  },
  {
    key: "analytics",
    name: "Analytics",
    desc: "Anonymised usage data to help us understand which pages are useful. We do not currently use any analytics cookies, but this control is here for future use.",
  },
  {
    key: "marketing",
    name: "Marketing",
    desc: "Advertising or retargeting cookies. We do not currently use any marketing cookies, but this control is here for future use.",
  },
];

function readSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.choices) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(choices) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ choices, ts: new Date().toISOString() })
  );
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = readSaved();
    if (!saved) {
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
    setPrefs({ functional: false, analytics: false, marketing: false, ...saved.choices, necessary: true });
  }, []);

  const acceptAll = () => {
    const all = { necessary: true, functional: true, analytics: true, marketing: true };
    persist(all);
    setPrefs(all);
    setVisible(false);
    setManageOpen(false);
  };

  const rejectAll = () => {
    const none = { necessary: true, functional: false, analytics: false, marketing: false };
    persist(none);
    setPrefs(none);
    setVisible(false);
    setManageOpen(false);
  };

  const savePreferences = () => {
    persist({ ...prefs, necessary: true });
    setVisible(false);
    setManageOpen(false);
  };

  const toggle = (key) => {
    if (key === "necessary") return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  if (!visible) return null;

  return (
    <>
      {/* BANNER */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-foreground text-background border-t border-primary shadow-2xl"
        data-testid="cookie-consent-bar"
        role="dialog"
        aria-label="Cookie preferences"
      >
        <div className="container-tight py-4 md:py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center relative">
          <div className="md:col-span-7">
            <div className="overline text-background/60 mb-1">Cookies</div>
            <p className="text-sm text-background/85 leading-relaxed max-w-3xl">
              We use cookies to run this site, keep you signed in and remember your preferences. You can accept all,
              reject all optional cookies, or manage each category individually.{" "}
              <Link to="/cookies" className="underline hover:text-secondary" data-testid="cookie-policy-link">
                Read our cookie policy
              </Link>
              .
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-2 md:justify-end">
            <button
              onClick={() => setManageOpen(true)}
              className="btn-sharp bg-transparent border border-background/40 text-background hover:bg-background hover:text-foreground uppercase tracking-[0.18em] text-[11px]"
              data-testid="cookie-manage-btn"
            >
              Manage
            </button>
            <button
              onClick={rejectAll}
              className="btn-sharp bg-transparent border border-background/40 text-background hover:bg-background hover:text-foreground uppercase tracking-[0.18em] text-[11px]"
              data-testid="cookie-reject-btn"
            >
              Reject all
            </button>
            <button
              onClick={acceptAll}
              className="btn-sharp bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-[11px]"
              data-testid="cookie-accept-btn"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>

      {/* MANAGE MODAL */}
      {manageOpen && (
        <div
          className="fixed inset-0 z-[60] bg-foreground/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => setManageOpen(false)}
          data-testid="cookie-manage-overlay"
        >
          <div
            className="bg-background w-full md:max-w-2xl border border-border max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cookie-manage-modal"
          >
            <div className="p-6 md:p-8 border-b border-border flex items-start justify-between gap-4">
              <div>
                <div className="overline">Cookie preferences</div>
                <h3 className="font-display text-3xl mt-2 leading-tight">Choose what you allow.</h3>
                <p className="mt-3 text-sm text-foreground/70 max-w-md">
                  Turn each category on or off. Strictly necessary cookies stay on because the site cannot run without
                  them.
                </p>
              </div>
              <button
                onClick={() => setManageOpen(false)}
                className="p-2 text-foreground/60 hover:text-foreground"
                aria-label="Close"
                data-testid="cookie-manage-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="divide-y divide-border">
              {CATEGORIES.map((c) => (
                <div key={c.key} className="p-6 md:p-8 flex items-start gap-6" data-testid={`cookie-category-${c.key}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="font-display text-xl">{c.name}</div>
                      {c.always && (
                        <span className="text-[10px] uppercase tracking-[0.18em] bg-muted px-2 py-1 text-foreground/60">
                          Always on
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{c.desc}</p>
                  </div>
                  <div className="shrink-0 pt-1">
                    <button
                      role="switch"
                      aria-checked={c.always ? true : !!prefs[c.key]}
                      aria-label={`Toggle ${c.name}`}
                      disabled={c.always}
                      onClick={() => toggle(c.key)}
                      className={`relative w-11 h-6 border transition-colors ${
                        c.always || prefs[c.key]
                          ? "bg-primary border-primary"
                          : "bg-transparent border-foreground/30"
                      } ${c.always ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`cookie-toggle-${c.key}`}
                    >
                      <span
                        className={`absolute top-[1px] w-[18px] h-[18px] transition-all ${
                          c.always || prefs[c.key]
                            ? "left-[22px] bg-background"
                            : "left-[2px] bg-foreground/50"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 md:p-8 border-t border-border flex flex-wrap gap-3 justify-between items-center">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={rejectAll}
                  className="btn-outline uppercase tracking-[0.18em] text-[11px]"
                  data-testid="cookie-manage-reject-all"
                >
                  Reject all
                </button>
                <button
                  onClick={acceptAll}
                  className="btn-outline uppercase tracking-[0.18em] text-[11px]"
                  data-testid="cookie-manage-accept-all"
                >
                  Accept all
                </button>
              </div>
              <button
                onClick={savePreferences}
                className="btn-primary uppercase tracking-[0.18em] text-[12px]"
                data-testid="cookie-save-preferences"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
